import {useAtomValue} from "jotai";
import {Farm} from "../types/domain/farm";
import {getAllFarms} from "../services/farmService";
import {useQuery} from "@tanstack/react-query";
import _ from "lodash";
import dayjs from "dayjs";
import {getInsightOverview} from "../services/insightOverviewService";
import {activeDateLoadable} from "../store/activeDateStore";
import {activeMergedFarmIds} from "../store/activeMergedFarmIds";
import {showActiveFieldsAtom} from "../store/filterSelectionStore";
import {useActiveCompanyId} from "./companiesHooks";
import {routerFarmIdAtom} from "../store/routerStore";
import {queryClient} from "../utils/queryClient";
import {useMemo} from "react";

export const useActiveFarm = (): Farm | undefined => {
    const activeCompanyId = useActiveCompanyId();
    const activeFarmId = useAtomValue(routerFarmIdAtom);
    const farmsQuery = useCompanyFarms(activeCompanyId);

    return activeFarmId ? farmsQuery.data?.mapByFarmId[activeFarmId] : undefined;
};

export const useCompanyFarms = (companyId?: string) => {
    return useQuery({
        queryKey: ["companyFarms", companyId],
        queryFn: async () => {
            const allFarms = await getAllFarms(companyId!);
            allFarms.sort(sortFarms);

            return {
                farms: allFarms,
                mapByFarmId: _.keyBy(allFarms, "id"),
            };
        },
        enabled: !!companyId,
    });
};

export const useActiveFarmOverviewData = () => {
    const companyId = useActiveCompanyId();

    const onlyActiveOrders = useAtomValue(showActiveFieldsAtom);
    const farmIds = useAtomValue(activeMergedFarmIds);

    /* Refactor this activeDateLoadableValue */
    const activeDateLoadableValue = useAtomValue(activeDateLoadable);
    const activeDate = useMemo(() => {
        return activeDateLoadableValue.state === "hasData" && activeDateLoadableValue.data
            ? activeDateLoadableValue.data
            : dayjs();
    }, [activeDateLoadableValue]);

    return useQuery({
        queryKey: ["farmOverview", companyId, activeDate?.toISOString(), onlyActiveOrders, farmIds],
        queryFn: () => getInsightOverview(companyId, activeDate, onlyActiveOrders, farmIds),
        enabled: !!companyId && farmIds.length > 0,
        gcTime: Infinity,
        staleTime: Infinity,
    });
};

const sortFarms = (left: Farm, right: Farm) => {
    if (left.isDefault) {
        return 1;
    } else if (right.isDefault) {
        return -1;
    } else {
        return left.name.localeCompare(right.name);
    }
};

export const invalidateAllCompanyFarmsHooks = (companyId: string) => {
    void queryClient.invalidateQueries({queryKey: ["companyFarms", companyId], exact: false});
    void queryClient.invalidateQueries({queryKey: ["farmOverview", companyId], exact: false});
};
